import { StandardEvents } from '@shopify/events';

// AllTerra Central — reactive buy CTA (<at-buy-cta>)
// Swaps the native Add-to-cart for a Rental / Speak-to-Expert button based on the
// active variant's condition, price visibility, hide-add-to-cart and
// speak-with-expert flags — each resolved server-side as "variant value, else
// product value". Variant metafields aren't in the variant JSON, so the block
// renders a variantId → {condition, showPrice, hideAtc, speakExpert} map and this reads it on
// Horizon's `shopify:product:select` event.
class AtBuyCta extends HTMLElement {
  connectedCallback() {
    try {
      this.modes = JSON.parse(this.dataset.modes || '{}');
    } catch {
      this.modes = {};
    }
    this.product = { title: this.dataset.productTitle || '', url: this.dataset.productUrl || '' };
    this.section = this.closest('.shopify-section') || document;
    this.nativeBuy = this.section.querySelector('.buy-buttons-block');
    this.rentalBtn = this.querySelector('[data-buy-rental]');
    this.expertBtn = this.querySelector('[data-buy-expert]');
    this.contactBtn = this.querySelector('[data-buy-contact]');

    this.rentalBtn?.addEventListener('click', () => this.openModal('rental'));
    this.expertBtn?.addEventListener('click', () => this.openModal('expert'));
    this.contactBtn?.addEventListener('click', () => this.openModal('contact'));

    this.onSelect = this.onSelect.bind(this);
    this.section.addEventListener(StandardEvents.productSelect, this.onSelect);

    this.apply(this.dataset.current);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.onSelect);
  }

  async onSelect(event) {
    try {
      const { variant } = await event.promise;
      // variant.id arrives as a GID (gid://shopify/ProductVariant/123); the modes
      // map is keyed by the numeric id, so normalise to the trailing segment.
      if (variant && variant.id != null) this.apply(String(variant.id).split('/').pop());
    } catch {
      /* unavailable combination — leave current mode */
    }
  }

  apply(variantId) {
    const mode = this.modes[variantId] || {};
    const isRental = String(mode.condition || '').toUpperCase() === 'RENTAL';
    const hidePrice = mode.showPrice === false;
    // A hidden price always hides Add-to-cart; hide_add_to_cart hides it while
    // the price stays visible. Either way "Speak to Expert" becomes the primary CTA.
    const hideAtc = hidePrice || mode.hideAtc === true;

    const soloBuy = isRental || hideAtc;
    if (this.nativeBuy) this.nativeBuy.style.display = soloBuy ? 'none' : '';
    if (this.rentalBtn) this.rentalBtn.hidden = !isRental;
    if (this.expertBtn) this.expertBtn.hidden = isRental || !hideAtc;
    // The secondary button coexists with Add-to-cart, so it would duplicate the
    // primary "Speak to Expert" shown whenever Add-to-cart is hidden.
    if (this.contactBtn) this.contactBtn.hidden = mode.speakExpert !== true || hideAtc;
    // When the native buy buttons are hidden this block becomes the primary buy
    // control and sits directly under the purchase notice, so it must NOT keep the
    // negative top margin meant to hug the Add-to-cart row (see at-pdp.css).
    this.classList.toggle('at-buy-cta--solo', soloBuy);
  }

  openModal(name) {
    this.dispatchEvent(
      new CustomEvent('at:open-modal', {
        bubbles: true,
        detail: { modal: name, product: this.product },
      })
    );
  }
}

if (!customElements.get('at-buy-cta')) {
  customElements.define('at-buy-cta', AtBuyCta);
}
